/**
 * `alpha_cut` → `BaseMaterial3D::Transparency` → three material state. Godot writes this switch
 * twice with identical arms (`sprite_3d.cpp:285-297`, `label_3d.cpp:386-393`), so it is one
 * decision here.
 */
import { drawsInShadowPass } from '../godot/rendering/shadowPass';
import { AlphaCutMode } from '../godot/rendering/materials/alphaCut';
import { hashAntialiasing, scissorAntialiasing, } from '../godot/rendering/materials/alphaAntialiasing';
/** Passed as `transparentFlag` by a node whose DrawFlags have no `FLAG_TRANSPARENT` member. */
export const NO_TRANSPARENT_FLAG = 'no-transparent-flag';
/** The opaque pass with no cut, which every arm starts from. */
const OPAQUE_ARM = {
    alphaTest: 0,
    alphaHash: false,
    depthPrepass: false,
    depthWrite: true,
    blended: false,
    opaqueAfterCut: false,
    antialiasing: null,
};
/**
 * A cut under alpha antialiasing: `uses_alpha_antialiasing` re-admits it to the alpha pass with a
 * depth prepass, and its shader keeps the ramped alpha (`scene_shader_forward_clustered.h:279-293`).
 */
const ANTIALIASED_ARM = {
    ...OPAQUE_ARM,
    depthPrepass: true,
    depthWrite: false,
    blended: true,
};
/** Godot's `mat_transparency` switch in three's terms. */
export function alphaCutSurface({ mode, scissorThreshold, transparentFlag, antialiasing, }) {
    // TRANSPARENCY_DISABLED is the initialiser the switch never reaches (`sprite_3d.cpp:284-286`).
    // Its shader writes no ALPHA, so the texture alpha never reaches the blend.
    if (transparentFlag === false) {
        return { ...OPAQUE_ARM, readsAlbedoAlpha: false };
    }
    return { ...cutArm(mode, scissorThreshold, antialiasing), readsAlbedoAlpha: true };
}
/** One arm of the switch, each of which multiplies the albedo alpha into ALPHA. */
function cutArm(mode, scissorThreshold, { mode: antialiasingMode, edge }) {
    // Without alpha antialiasing, SCISSOR and HASH force `alpha = 1.0` past the cut
    // (`scene_forward_clustered.glsl:1413-1415`), so they land in the opaque list
    // (`scene_shader_forward_clustered.cpp:252`), which writes depth.
    switch (mode) {
        case AlphaCutMode.DISCARD: {
            const antialiasing = scissorAntialiasing(antialiasingMode, scissorThreshold, edge);
            if (antialiasing)
                return { ...ANTIALIASED_ARM, antialiasing };
            return { ...OPAQUE_ARM, alphaTest: scissorThreshold, opaqueAfterCut: true };
        }
        case AlphaCutMode.HASH: {
            const antialiasing = hashAntialiasing(antialiasingMode, edge);
            if (antialiasing)
                return { ...ANTIALIASED_ARM, alphaHash: true, antialiasing };
            return { ...OPAQUE_ARM, alphaHash: true, opaqueAfterCut: true };
        }
        case AlphaCutMode.OPAQUE_PREPASS:
            // Blended and uncut in colour. `get_material_for_2d` keeps DEPTH_DRAW_OPAQUE_ONLY, so only
            // the depth prepass writes depth.
            return { ...OPAQUE_ARM, depthPrepass: true, depthWrite: false, blended: true };
        default:
            // DISABLED. `depth_draw_opaque` on a blended surface writes no depth (`material.cpp:800`).
            return { ...OPAQUE_ARM, depthWrite: false, blended: true };
    }
}
/** Whether the surface joins the shadow pass. No depth test sends any surface to the alpha pass. */
export function joinsShadowPass(cut, depthTest) {
    return drawsInShadowPass({
        alphaPass: cut.blended || !depthTest,
        depthInAlphaPass: cut.depthPrepass && depthTest,
    });
}
//# sourceMappingURL=godotAlphaCut.js.map